"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useAuth } from "@tillix/tx-core/react";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import { FormProvider, useForm } from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";
import OrSeparator from "../../../components/tenant/howard-county/components/OrSeparator";
import LoginWithPasskey from "../atoms/LoginWithPasskey";
import MagicLinkButton from "../atoms/MagicLinkButton";
import CheckboxField from "../fields/CheckboxField";
import EmailInput from "../fields/EmailInput";
import PasswordInput from "../fields/PasswordInput";
import { defaultSignInFormValues, SignInFormSchema } from "../schemas/sign-in";
import { sendMagicLink } from "../utils/send-magic-link";
import type { TenantAuthConfig } from "../config/tenant-auth";
import type { SignInFormValues } from "../schemas/sign-in";

interface SignInFormProps {
  authConfig: TenantAuthConfig;
  hasMagicLinkError?: boolean;
}

export default function SignInForm({
  authConfig,
  hasMagicLinkError = false,
}: SignInFormProps) {
  const authStore = useAuth();
  const router = useRouter();
  const { t } = useT();
  const { t: tErrors } = useT("errors");

  const hasPassword = authConfig.allowedMethods.includes("password");
  const hasMagicLink = authConfig.allowedMethods.includes("magic-link");
  const hasPasskey = authConfig.allowedMethods.includes("passkey");
  const hasMultipleMethods = authConfig.allowedMethods.length > 1;

  const [useMagicLink, setUseMagicLink] = useState(
    authConfig.defaultMethod === "magic-link",
  );

  const form = useForm<SignInFormValues>({
    resolver: arktypeResolver(SignInFormSchema),
    defaultValues: {
      ...defaultSignInFormValues,
      password: authConfig.defaultMethod === "magic-link" ? undefined : "",
    },
    mode: "onChange",
  });

  const {
    formState: { isSubmitting, isValid, errors },
  } = form;
  const rootError = errors.root?.message;

  // handle magic link error (better auth redirects with ?token=XXX on failure)
  useEffect(() => {
    if (!hasMagicLinkError) return;
    form.setError("root", {
      message: t("auth.magicLinkExpired"),
    });
    setUseMagicLink(true);
    form.setValue("password", undefined, { shouldValidate: true });
    router.replace("/sign-in", { scroll: false });
  }, [hasMagicLinkError, form, router, t]);

  const handleFormSubmit = async (values: SignInFormValues) => {
    if (useMagicLink) {
      await sendMagicLink({
        authStore,
        form,
        email: values.email,
        mode: "sign-in",
        t,
      });
      return;
    }

    if (!values.password) {
      form.setError("root", { message: tErrors("auth.PASSWORD_REQUIRED") });
      return;
    }

    const result = await authStore.signIn({
      email: values.email,
      password: values.password,
      rememberMe: values.keepMeSignedInFor30Days ?? false,
    });

    if (result.error) {
      console.log("failed to sign in, error:", {
        code: result.error.code,
        message: result.error.message,
      });
      form.setError("root", {
        message: result.error.code ?? result.error.message,
      });
    } else {
      console.log("signed in successfully");
      router.push("/dashboard");
    }
  };

  const renderMagicLinkContent = () => {
    return (
      <>
        <p className="text-muted-foreground text-sm">
          {t("auth.magicLinkDescription")}
        </p>
        <MagicLinkButton<SignInFormValues> />
      </>
    );
  };

  const renderPasswordContent = () => {
    return (
      <>
        <PasswordInput<SignInFormValues> />
        <div className="flex items-center justify-between">
          <CheckboxField<SignInFormValues> name="keepMeSignedInFor30Days" />
        </div>
        <Button
          className="w-full"
          disabled={isSubmitting || !isValid}
          type="submit"
        >
          {isSubmitting ? t("auth.signingIn") : t("application.continue")}
        </Button>
      </>
    );
  };

  const toggleMagicLinkMode = () => {
    form.clearErrors("root");
    setUseMagicLink((prev) => {
      const isCurrentlyMagicLink = prev;
      if (!isCurrentlyMagicLink) {
        form.setValue("password", undefined, { shouldValidate: true });
      } else {
        form.setValue("password", "", { shouldValidate: true });
      }
      return !isCurrentlyMagicLink;
    });
  };

  const toggleButtonText = useMagicLink
    ? t("auth.usePasswordInstead")
    : t("auth.useMagicLinkInstead");

  return (
    <FormProvider {...form}>
      <Form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(handleFormSubmit)}
      >
        <EmailInput<SignInFormValues> />

        {useMagicLink ? renderMagicLinkContent() : renderPasswordContent()}
        <FormRootError keyPrefix="auth" rootError={rootError} />

        {hasMultipleMethods && (
          <>
            <OrSeparator />
            {hasPassword && hasMagicLink && (
              <Button
                onClick={toggleMagicLinkMode}
                type="button"
                variant="outline"
              >
                {toggleButtonText}
              </Button>
            )}
            {hasPasskey && <LoginWithPasskey<SignInFormValues> />}
          </>
        )}
      </Form>
    </FormProvider>
  );
}
